import axios, { AxiosInstance, AxiosResponse } from "axios"
import { BASE_URL } from "../../constants"
import { Logger } from "./logger"
import { container } from "../../infrastructure/container"

/**
 * Interface defining methods for making HTTP requests.
 */
export interface HttpService {
  /**
   * Performs a GET request.
   * @param route - The API endpoint (e.g., `/users`).
   * @param params - Optional query parameters.
   * @returns A promise resolving to the expected response type `Res`.
   * @throws If an error occurs, throws an error of type `Err`.
   */
  get<Res, Err extends { message: string }>(
    route: string,
    params?: Record<string, string>
  ): Promise<Res>

  /**
   * Performs a POST request.
   * @param route - The API endpoint (e.g., `/login`).
   * @param data - The request payload of type `Req`.
   * @param params - Optional query parameters.
   * @returns A promise resolving to the expected response type `Res`.
   * @throws (Err) If an error occurs, throws an error of type `Err`.
   */
  post<Req, Res, Err extends { message: string }>(
    route: string,
    data: Req,
    params?: Record<string, string>
  ): Promise<Res>
}

/**
 * Implementation of `HttpService` that uses Axios for making HTTP requests.
 */
export class AppHttpService implements HttpService {
  private http: AxiosInstance
  private logger: Logger

  /**
   * Initializes the HTTP service with Axios instance.
   */
  constructor() {
    const logger = container.resolve<Logger>("logger")
    this.logger = logger

    this.http = axios.create({
      baseURL: BASE_URL,
      headers: { "Content-Type": "application/json" },
      withCredentials: true, // Enables sending cookies for authentication
    })
  }

  async get<Res, Err extends { message: string }>(
    route: string,
    params?: Record<string, string>
  ): Promise<Res> {
    try {
      const fullUrl = this.buildUrl(route, params)
      const response: AxiosResponse<Res> = await this.http.get(fullUrl, {
        withCredentials: true,
      })
      return response.data
    } catch (error: any) {
      throw this.handleError<Err>(error)
    }
  }

  async post<Req, Res, Err extends { message: string }>(
    route: string,
    data: Req,
    params?: Record<string, string>
  ): Promise<Res> {
    try {
      const response: AxiosResponse<Res> = await this.http.post(route, data, {
        params,
        withCredentials: true,
      })
      return response.data
    } catch (error: any) {
      throw this.handleError<Err>(error)
    }
  }

  /**
   * Handles API errors by mapping them to the expected `Err` type.
   * If the error response is not structured as expected, a default error is returned.
   * @param error - The original error object from Axios.
   * @returns The parsed error object of type `Err`.
   */
  private handleError<Err extends { message: string }>(error: any): Err {
    this.logger.error(`🚨 API Error:`, error)

    if (error.response?.data) {
      try {
        return error.response.data as Err
      } catch {
        return { message: "Unexpected error format" } as Err
      }
    }

    return { message: error.message || "Unknown error" } as Err
  }

  /**
   * Builds a URL by appending the values of the provided parameters to the base path.
   *
   * @param {string} basePath - The base route or endpoint (e.g., "/users").
   * @param {Record<string, string>} params - An object containing key-value pairs,
   *                                          where only the values will be appended to the URL.
   * @returns {string} - The constructed URL with values as path parameters.
   *
   * @example
   * const route = "/users"
   * const params = { id: "123", action: "edit" }
   *
   * buildUrl(route, params) // Returns "/users/123/edit"
   */
  private buildUrl(basePath: string, params: Record<string, string>): string {
    if (!params || Object.keys(params).length === 0) return basePath
    const queryString = Object.values(params).join("/")
    return `${basePath}/${queryString}`
  }
}
